export interface RecentlyVisitedCustomer {
  customerId: string;
  name: string;
  visitedAt: number;
}

const STORAGE_KEY_PREFIX = 'admin_recently_visited_customers';
const MAX_RECENT_CUSTOMERS = 6;

const getStorageKey = (userId: number | undefined): string => {
  if (!userId) {
    throw new Error('User ID is required to access recently visited customers');
  }
  return `${STORAGE_KEY_PREFIX}_${userId}`;
};

const obfuscate = (data: string): string => {
  try {
    const encoded = btoa(unescape(encodeURIComponent(data)));
    return btoa(encoded.split('').reverse().join(''));
  } catch (error) {
    console.error('Failed to obfuscate data:', error);
    return data;
  }
};

const deobfuscate = (obfuscated: string): string => {
  try {
    const decoded = atob(obfuscated);
    const reversed = decoded.split('').reverse().join('');
    return decodeURIComponent(escape(atob(reversed)));
  } catch (error) {
    console.error('Failed to deobfuscate data:', error);
    return obfuscated;
  }
};

export const getRecentlyVisitedCustomers = (
  userId: number | undefined,
): RecentlyVisitedCustomer[] => {
  try {
    if (!userId) return [];

    const storageKey = getStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    if (!stored) return [];

    const deobfuscated = deobfuscate(stored);
    const parsed = JSON.parse(deobfuscated) as RecentlyVisitedCustomer[];
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (item) =>
          item &&
          typeof item.customerId === 'string' &&
          typeof item.name === 'string' &&
          typeof item.visitedAt === 'number',
      );
    }
    return [];
  } catch (error) {
    console.error('Failed to get recently visited customers:', error);
    return [];
  }
};

export const addRecentlyVisitedCustomer = (
  userId: number | undefined,
  customerId: string,
  name: string,
): void => {
  try {
    if (!userId) {
      console.warn('Cannot add recently visited customer: user ID is required');
      return;
    }

    const existing = getRecentlyVisitedCustomers(userId);

    // Remove if already exists (to avoid duplicates)
    const filtered = existing.filter(
      (customer) => customer.customerId !== customerId,
    );

    const updated = [
      { customerId, name, visitedAt: Date.now() },
      ...filtered,
    ].slice(0, MAX_RECENT_CUSTOMERS);

    const storageKey = getStorageKey(userId);
    const obfuscated = obfuscate(JSON.stringify(updated));
    localStorage.setItem(storageKey, obfuscated);
  } catch (error) {
    console.error('Failed to save recently visited customer:', error);
  }
};
